export const PERMISSION_MODULES = [
  "Transport",
  "Fleet",
  "Consignments",
  "Accounting",
  "Parties",
  "InventoryIssue",
  "Users"
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number];
export type PermissionAction = "Read" | "Create" | "Update" | "Delete";

export const ADMIN_ROLES = ["admin", "administrator", "superadmin"];

export function isAdminRole(roles: string[]): boolean {
  return roles.some((r) => ADMIN_ROLES.includes(r.toLowerCase()));
}

/**
 * Mirrors the backend PermissionHandler logic exactly.
 * - Admin role → always true
 * - No permissions configured (empty array) → allow all (matches nav behaviour)
 * - Exact match → true
 * - Implied: having Delete implies Update+Create+Read; having Update implies Create+Read; having Create implies Read
 */
export function hasPermission(
  userPermissions: string[],
  userRoles: string[],
  requiredPermission: string
): boolean {
  if (isAdminRole(userRoles)) return true;
  if (userPermissions.length === 0) return true; // not yet configured → open

  if (userPermissions.some((p) => p.toLowerCase() === requiredPermission.toLowerCase())) return true;

  const parts = requiredPermission.split(".");
  if (parts.length !== 2) return false;
  const [module, action] = parts;

  switch (action) {
    case "Read":
      return ["Create", "Update", "Delete"].some((a) =>
        userPermissions.some((p) => p.toLowerCase() === `${module}.${a}`.toLowerCase())
      );
    case "Create":
      return ["Update", "Delete"].some((a) =>
        userPermissions.some((p) => p.toLowerCase() === `${module}.${a}`.toLowerCase())
      );
    case "Update":
      return userPermissions.some((p) => p.toLowerCase() === `${module}.Delete`.toLowerCase());
    default:
      return false;
  }
}

/** Returns which actions the user has (or implied) for each module. */
export function getPermissionSummary(
  userPermissions: string[],
  userRoles: string[]
): Record<PermissionModule, PermissionAction[]> {
  const actions: PermissionAction[] = ["Read", "Create", "Update", "Delete"];
  const result = {} as Record<PermissionModule, PermissionAction[]>;

  for (const module of PERMISSION_MODULES) {
    result[module] = actions.filter((action) =>
      hasPermission(userPermissions, userRoles, `${module}.${action}`)
    );
  }
  return result;
}

/** Human-readable label for a permission string like "Consignments.Read". */
export function permissionLabel(permission: string): string {
  const [module, action] = permission.split(".");
  return `${module ?? permission} › ${action ?? ""}`;
}

/** Human-readable description of what an action level means. */
export const ACTION_DESCRIPTIONS: Record<PermissionAction, string> = {
  Read: "View records",
  Create: "Add new records",
  Update: "Edit existing records",
  Delete: "Remove records"
};
